import { logOut } from "./userAuthActions";

import {
	OPEN_PERSISTENT_ERROR_SNACKBAR,
	FOLDER_NAME_CONFLICT,
	BULK_DOWNLOAD_FAILURE,
	PASTE_FAILURE,
	UPLOAD_NEW_VERSION_FAILURE,
} from "./types";

import store from "../store";

const defaultErrorMessage = "LexWorkplace encountered an error";

export function isPreventActionOnDeletedResource(errorCode) {
	return errorCode >= 2000 && errorCode < 2100;
}

export function isRateLimitError(err) {
	return err?.lxwError?.errorKey === "RATE_LIMIT_EXCEEDED";
}

export function isPreventArchiveAction(errorCode) {
	return [6009, 6008].includes(errorCode);
}

export function handleLexworkplaceAPIError(
	error,
	errorData,
	errorHandlingArgs
) {
	console.error("A LexWorkplace API error was encountered", {
		error,
		errorData,
		errorHandlingArgs,
	});

	const { code, userMessage, description, errorKey } = error;
	if (isPreventActionOnDeletedResource(code)) {
		// something is marked as deleted/trashed
		// prevent editing it and show message to user. Do not redirect them

		// remove loading blocker
		store.dispatch({
			// we just need _FAILURE but this may point someone to this particular spot
			type: "IN_TRASH_BLOCK_FAILURE",
		});
		showError(errorData?.userMessage || userMessage);
	}
	if (code >= 3000 && code <= 3100) {
		//unconnectable database, invalid config, etc.  API unusable
		showError(userMessage);
		redirectToHomepage();
	}

	if (code >= 6000 && code <= 6005) {
		insufficientMatterPermissionsError(error, errorHandlingArgs);
	} else if (code === 6060 || code === 6061 || code === 6080) {
		showError(userMessage);
	} else if (code === 6200) {
		store.dispatch({ type: FOLDER_NAME_CONFLICT, payload: errorData });
	} else if (code === 6300) {
		store.dispatch({
			type: BULK_DOWNLOAD_FAILURE,
			payload: { message: "error in export matter" },
		});
	} else if (code >= 6401 && code <= 6403) {
		store.dispatch({
			type: PASTE_FAILURE,
			payload: { message: description },
		});
	} else if (code >= 6600 && code <= 6601) {
		showError(description + ": " + JSON.stringify(errorData));
	} else if (errorKey === "USER_IS_INACTIVE") {
		// todo: any individual code is probably better switched to use errorKey for readability
		// the ranges are useful perhaps
		logOut()(store.dispatch);
		setTimeout(() => showError(userMessage), 50);
	} else if (errorKey === "TENANT_IS_SUSPENDED") {
		logOut()(store.dispatch);
		setTimeout(() => showError(userMessage), 50);
	} else if (errorKey === "TENANT_SUSPEND_BLOCKED_BY_ACTIVE_USERS") {
		showError(userMessage);
	} else if (errorKey === "ARCHIVE_CANNOT_BE_DISABLED_WITH_ARCHIVED_ITEMS") {
		showError(userMessage);
	} else if (errorKey === "TENANT_SUSPENDED_ACTION_BLOCKED") {
		showError(userMessage);
	} else if (
		errorKey === "TENANT_WIPE_REQUIRES_SUSPENDED_TENANT" ||
		errorKey === "TENANT_WIPE_BLOCKED_BY_ENABLED_USERS"
	) {
		showError(userMessage);
	} else if (errorKey === "RATE_LIMIT_EXCEEDED") {
		// background polling (e.g. job progress) retries on its own, don't show it
		if (!errorHandlingArgs?.suppressRateLimitSnackbar) {
			showError(userMessage);
		}
	} else if (code >= 5001 && code <= 5005) {
		/*
			The logged-in users tenant, or user record does not exist
			in either the global or regional database

			There is something critically wrong, log them out.

			This could happen if the developer is switching between
			environments while running locally, for example
		*/

		logOut()(store.dispatch);

		//logOut clears the entire redux state, including the global snackbar visibility
		//using setTimeout to ensure snackbar error message shows
		setTimeout(() => showError(userMessage), 50);
	} else if (code === 7003) {
		showError("Share email could not be resent");
	} else if (code === 6006 || code === 6007) {
		insufficientFeatureCodeErrorMessage(
			error.description,
			errorHandlingArgs
		);
	} else if (code === 8500 || code === 8501) {
		store.dispatch({
			type: UPLOAD_NEW_VERSION_FAILURE,
			payload: {
				message: `New version could not be uploaded because ${description}`,
			},
		});
	} else if (code >= 8600 && code <= 8603) {
		showError(description);
	} else if (code === 6008) {
		// insufficient permissions for a feature. There should be information in the error data to use in the error message
		if (errorData?.userMessage) {
			showError(errorData.userMessage);
		}
		if (
			[
				"CREATE_CLIENT_ALLOWED",
				"CREATE_MATTER_ALLOWED",
				"CAN_PERMANENT_DELETE",
			].includes(errorData?.featureCode)
		) {
			redirectToHomepage();
		}
	} else if (code === 6071) {
		if (errorData?.userMessage) {
			showError(errorData.userMessage);
		}
	} else if ([7004, 7005, 7006, 7007].includes(code)) {
		if (errorData?.userMessage) {
			showError(errorData.userMessage);
		} else {
			showError(
				"Sorry, this operation could not be completed due to an unexpected error."
			);
		}
	} else if (code === 9400 || code === 9401) {
		insufficientFeatureCodeErrorMessage(
			"This chat does not exist or you do not have access to it.",
			errorHandlingArgs
		);
	} else if (
		code === 9402 ||
		code === 9403 ||
		code === 9406 ||
		code === 9407 ||
		code === 9408
	) {
		showError(userMessage);
	} else if (code === 6009) {
		showError(userMessage);
	}
}

function insufficientMatterPermissionsError(error, errorHandlingArgs) {
	var dontRedirectToHomepage = false;

	if (errorHandlingArgs) {
		dontRedirectToHomepage = errorHandlingArgs.dontRedirectToHomepage;
	}

	const matterPermissionErrorMessage =
		"You do not have sufficient permissions to access this matter.\nPlease contact your firm administrator for more information";
	showError(matterPermissionErrorMessage);

	if (!dontRedirectToHomepage) {
		redirectToHomepage();
	}
}

function insufficientFeatureCodeErrorMessage(error, errorHandlingArgs) {
	var dontRedirectToHomepage = false;

	if (errorHandlingArgs) {
		dontRedirectToHomepage = errorHandlingArgs.dontRedirectToHomepage;
	}

	showError(error);

	if (!dontRedirectToHomepage) {
		redirectToHomepage();
	}
}

function showError(message = defaultErrorMessage) {
	store.dispatch({
		type: OPEN_PERSISTENT_ERROR_SNACKBAR,
		payload: {
			message,
		},
	});
}

function redirectToHomepage() {
	window.globalHistory.push("/");
}
